import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import {
  QuizQuestionAnswerMatching,
  QuizQuestionAnswerMultipleChoice,
  type QuizQuestionAnswer,
  QuizQuestionAnswerSequencing,
  QuizQuestionAnswerSingleChoice,
  QuizQuestionAnswerTrueFalse
} from '@/core/domain/quiz/QuizQuestionAnswer.ts'
import {
  QuizQuestionMatchingSchema,
  QuizQuestionMultipleChoiceSchema,
  type LearningQuestionEntity,
  QuizQuestionSequencingSchema,
  QuizQuestionSingleChoiceSchema,
  QuizQuestionTrueFalseSchema
} from '@/core/infrastructure/schemas/quiz/QuizQuestionSchema.ts'
import {
  isMatchingAnswer,
  isMultipleChoiceAnswer,
  isSequencingAnswer,
  isSingleChoiceAnswer,
  isTrueFalseAnswer
} from '@/core/infrastructure/schemas/quiz/QuizSchema.ts'
import { type ContentItemEntity } from '@/core/infrastructure/schemas/quiz/QuizQuestionAnswerSchema.ts'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'

/**
 * Factory pour créer une nouvelle instance de question du type spécifié
 * en préservant les données de base (id, label, order, feedbacks)
 */
export function createQuizQuestionOfType(
  sourceQuestion: QuizQuestion,
  newType: LearningQuizQuestionType.Values
): QuizQuestion {
  const baseData = {
    id: sourceQuestion.id,
    label: sourceQuestion.label,
    order: sourceQuestion.order,
    type: newType,
    feedbacks: { ...sourceQuestion.feedbacks },
    answers: []
  }

  switch (newType) {
    case LearningQuizQuestionType.Values.SINGLE_CHOICE:
      return new LearningQuizQuestionSingleChoice(baseData as any)
    
    case LearningQuizQuestionType.Values.MULTIPLE_CHOICE:
      return new LearningQuizQuestionMultipleChoice(baseData as any)
    
    case LearningQuizQuestionType.Values.TRUE_FALSE: {
      const trueFalseData = {
        ...baseData,
        answers: [
          {
            label: 'Vrai',
            isCorrect: true,
            type: LearningQuizQuestionType.Values.TRUE_FALSE
          },
          {
            label: 'Faux',
            isCorrect: false,
            type: LearningQuizQuestionType.Values.TRUE_FALSE
          }
        ]
      }
      return new LearningQuizQuestionTrueFalse(trueFalseData as any)
    }
    
    case LearningQuizQuestionType.Values.MATCHING:
      return new LearningQuizQuestionMatching(baseData as any)
    
    case LearningQuizQuestionType.Values.SEQUENCING:
      return new LearningQuizQuestionSequencing(baseData as any)
    
    default:
      throw new Error(`Type de question non supporté: ${newType}`)
  }
}

export abstract class QuizQuestion {
  id: number
  label: string
  order: number
  type: LearningQuizQuestionType.Values
  feedbacks: { correct: string; wrong: string }
  answers: QuizQuestionAnswer[]

  protected constructor(question: LearningQuestionEntity) {
    this.id = question.id
    this.label = question.label
    this.order = question.order
    this.type = question.type
    this.feedbacks = question.feedbacks
    this.answers = []
  }

  setOrder(order: number): void {
    this.order = order
  }

  clone(): QuizQuestion {
    // Sérialiser les réponses pour qu'elles soient compatibles avec le constructeur
    const serializedAnswers = this.answers.map((answer) => this.serializeAnswer(answer))

    const questionData = {
      id: this.id,
      label: this.label,
      order: this.order,
      type: this.type,
      feedbacks: { ...this.feedbacks },
      answers: serializedAnswers
    }

    return new (this.constructor as new (...args: any[]) => QuizQuestion)(questionData)
  }

  /**
   * Sérialise une réponse pour qu'elle soit compatible avec le constructeur
   * @param answer La réponse à sérialiser
   * @returns L'objet sérialisé compatible avec les schémas
   */
  private serializeAnswer(answer: QuizQuestionAnswer): any {
    const baseAnswer = {
      type: answer.type
    }

    // Sérialisation selon le type de réponse
    if ('label' in answer && 'isCorrect' in answer) {
      // SingleChoice, MultipleChoice, TrueFalse
      return {
        ...baseAnswer,
        label: (answer as any).label,
        isCorrect: (answer as any).isCorrect
      }
    } else if ('left' in answer && 'right' in answer) {
      // Matching
      return {
        ...baseAnswer,
        left: (answer as any).left,
        right: (answer as any).right
      }
    } else if ('data' in answer && 'order' in answer) {
      // Sequencing
      return {
        ...baseAnswer,
        data: (answer as any).data,
        order: (answer as any).order
      }
    }

    // Fallback pour des types non reconnus
    return baseAnswer
  }

  isValid(): boolean {
    return (
      !!this.label &&
      !!this.type &&
      this.answers.length > 0 &&
      this.answers.every((answer) => answer.isValid())
    )
  }

  /**
   * Retourne les erreurs de validation de la question
   * @returns Un tableau des erreurs de validation
   */
  getValidationErrors(): string[] {
    const errors: string[] = []

    if (!this.label) {
      errors.push('Le libellé de la question est obligatoire')
    }

    if (!this.type) {
      errors.push('Le type de question est obligatoire')
    }

    if (this.answers.length === 0) {
      errors.push('Au moins une réponse est obligatoire')
    }

    this.answers.forEach((answer, index) => {
      if (!answer.isValid()) {
        errors.push(`La réponse ${index + 1} est invalide`)
      }
    })

    return errors
  }
}

// Classes pour les différents types de questions
export class LearningQuizQuestionSingleChoice extends QuizQuestion {
  answers: QuizQuestionAnswerSingleChoice[]

  constructor(
    question: Extract<
      LearningQuestionEntity,
      { type: LearningQuizQuestionType.Values.SINGLE_CHOICE }
    >
  ) {
    super(question)
    this.answers = question.answers
      .filter(isSingleChoiceAnswer)
      .map((answer) => QuizQuestionAnswerSingleChoice.create(answer))
  }

  getCorrectAnswer(): QuizQuestionAnswerSingleChoice | undefined {
    return this.answers.find((answer) => answer.isCorrect)
  }

  isValid(): boolean {
    return super.isValid() && this.answers.length >= 2 && this.getCorrectAnswers().length === 1
  }

  getCorrectAnswers(): QuizQuestionAnswerSingleChoice[] {
    return this.answers.filter((answer) => answer.isCorrect)
  }

  getValidationErrors(): string[] {
    const errors = super.getValidationErrors()

    if (this.answers.length < 2) {
      errors.push('Une question à choix unique doit avoir au moins 2 réponses')
    }

    const correctAnswers = this.getCorrectAnswers()
    if (correctAnswers.length === 0) {
      errors.push('Une question à choix unique doit avoir exactement une réponse correcte')
    } else if (correctAnswers.length > 1) {
      errors.push("Une question à choix unique ne peut avoir qu'une seule réponse correcte")
    }

    return errors
  }

  public static createEmpty(): LearningQuizQuestionSingleChoice {
    return new LearningQuizQuestionSingleChoice({
      id: 0,
      label: '',
      order: 0,
      type: LearningQuizQuestionType.Values.SINGLE_CHOICE,
      feedbacks: { correct: '', wrong: '' },
      answers: []
    })
  }

  public static create(data: unknown): LearningQuizQuestionSingleChoice {
    return EntityValidator.validateAndCreate(
      QuizQuestionSingleChoiceSchema,
      data,
      LearningQuizQuestionSingleChoice,
      'SingleChoiceQuestion'
    )
  }
}

export class LearningQuizQuestionMultipleChoice extends QuizQuestion {
  answers: QuizQuestionAnswerMultipleChoice[]

  constructor(
    question: Extract<
      LearningQuestionEntity,
      { type: LearningQuizQuestionType.Values.MULTIPLE_CHOICE }
    >
  ) {
    super(question)
    this.answers = question.answers
      .filter(isMultipleChoiceAnswer)
      .map((answer) => QuizQuestionAnswerMultipleChoice.create(answer))
  }

  getCorrectAnswers(): QuizQuestionAnswerMultipleChoice[] {
    return this.answers.filter((answer) => answer.isCorrect)
  }

  isValid(): boolean {
    return super.isValid() && this.answers.length >= 2 && this.getCorrectAnswers().length >= 1
  }

  getValidationErrors(): string[] {
    const errors = super.getValidationErrors()

    if (this.answers.length < 2) {
      errors.push('Une question à choix multiples doit avoir au moins 2 réponses')
    }

    const correctAnswers = this.getCorrectAnswers()
    if (correctAnswers.length === 0) {
      errors.push('Une question à choix multiples doit avoir au moins une réponse correcte')
    }

    return errors
  }

  public static createEmpty(): LearningQuizQuestionMultipleChoice {
    return new LearningQuizQuestionMultipleChoice({
      id: 0,
      label: '',
      order: 0,
      type: LearningQuizQuestionType.Values.MULTIPLE_CHOICE,
      feedbacks: { correct: '', wrong: '' },
      answers: []
    })
  }

  public static create(data: unknown): LearningQuizQuestionMultipleChoice {
    return EntityValidator.validateAndCreate(
      QuizQuestionMultipleChoiceSchema,
      data,
      LearningQuizQuestionMultipleChoice,
      'MultipleChoiceQuestion'
    )
  }
}

export class LearningQuizQuestionTrueFalse extends QuizQuestion {
  answers: QuizQuestionAnswerTrueFalse[]

  constructor(
    question: Extract<LearningQuestionEntity, { type: LearningQuizQuestionType.Values.TRUE_FALSE }>
  ) {
    super(question)
    this.answers = question.answers
      .filter(isTrueFalseAnswer)
      .map((answer) => QuizQuestionAnswerTrueFalse.create(answer))
  }

  getCorrectAnswer(): QuizQuestionAnswerTrueFalse | undefined {
    return this.answers.find((answer) => answer.isCorrect)
  }

  public static createEmpty(): LearningQuizQuestionTrueFalse {
    return new LearningQuizQuestionTrueFalse({
      id: 0,
      label: '',
      order: 0,
      type: LearningQuizQuestionType.Values.TRUE_FALSE,
      feedbacks: { correct: '', wrong: '' },
      answers: [
        {
          label: 'Vrai',
          isCorrect: true,
          type: LearningQuizQuestionType.Values.TRUE_FALSE
        },
        {
          label: 'Faux',
          isCorrect: false,
          type: LearningQuizQuestionType.Values.TRUE_FALSE
        }
      ]
    })
  }

  public static create(data: unknown): LearningQuizQuestionTrueFalse {
    return EntityValidator.validateAndCreate(
      QuizQuestionTrueFalseSchema,
      data,
      LearningQuizQuestionTrueFalse,
      'TrueFalseQuestion'
    )
  }
}

export class LearningQuizQuestionMatching extends QuizQuestion {
  answers: QuizQuestionAnswerMatching[]

  constructor(
    question: Extract<LearningQuestionEntity, { type: LearningQuizQuestionType.Values.MATCHING }>
  ) {
    super(question)
    this.answers = question.answers
      .filter(isMatchingAnswer)
      .map((answer) => QuizQuestionAnswerMatching.create(answer))
  }

  getPairs(): { left: ContentItemEntity; right: ContentItemEntity }[] {
    return this.answers.map((answer) => ({
      left: answer.left,
      right: answer.right
    }))
  }

  isValid(): boolean {
    return super.isValid() && this.answers.length >= 2
  }

  getValidationErrors(): string[] {
    const errors = super.getValidationErrors()

    if (this.answers.length < 2) {
      errors.push("Une question d'association doit avoir au moins 2 paires à associer")
    }

    return errors
  }

  public static createEmpty(): LearningQuizQuestionMatching {
    return new LearningQuizQuestionMatching({
      id: 0,
      label: '',
      order: 0,
      type: LearningQuizQuestionType.Values.MATCHING,
      feedbacks: { correct: '', wrong: '' },
      answers: []
    })
  }

  public static create(data: unknown): LearningQuizQuestionMatching {
    return EntityValidator.validateAndCreate(
      QuizQuestionMatchingSchema,
      data,
      LearningQuizQuestionMatching,
      'MatchingQuestion'
    )
  }
}

export class LearningQuizQuestionSequencing extends QuizQuestion {
  answers: QuizQuestionAnswerSequencing[]

  constructor(
    question: Extract<LearningQuestionEntity, { type: LearningQuizQuestionType.Values.SEQUENCING }>
  ) {
    super(question)
    this.answers = question.answers
      .filter(isSequencingAnswer)
      .map((answer) => QuizQuestionAnswerSequencing.create(answer))
  }

  getOrderedItems(): ContentItemEntity[] {
    return [...this.answers].sort((a, b) => a.order - b.order).map((answer) => answer.data)
  }

  isValid(): boolean {
    return super.isValid() && this.answers.length >= 2
  }

  getValidationErrors(): string[] {
    const errors = super.getValidationErrors()

    if (this.answers.length < 2) {
      errors.push('Une question de séquençage doit avoir au moins 2 éléments à ordonner')
    }

    return errors
  }

  public static createEmpty(): LearningQuizQuestionSequencing {
    return new LearningQuizQuestionSequencing({
      id: 0,
      label: '',
      order: 0,
      type: LearningQuizQuestionType.Values.SEQUENCING,
      feedbacks: { correct: '', wrong: '' },
      answers: []
    })
  }

  public static create(data: unknown): LearningQuizQuestionSequencing {
    return EntityValidator.validateAndCreate(
      QuizQuestionSequencingSchema,
      data,
      LearningQuizQuestionSequencing,
      'SequencingQuestion'
    )
  }
}
